/**
 * Smart Daily Planner Service
 * AkÄ±llÄ± gÃ¼nlÃ¼k plan oluÅŸturma ve yÃ¶netim servisi
 */

import {
    DailyTask,
    DailyPlan,
    SmartPlannerConfig,
    StudyTempo,
    Department,
    DayOfWeek,
    StudyPlanCurriculum
} from '../types';

import {
    TEMPO_CONFIG,
    GRADE_CONFIG,
    YKS_TIMELINE,
    PLANNER_SETTINGS,
    SUBJECTS_BY_FIELD,
    SUBJECT_COLORS,
    DAY_NAMES,
    EXAM_TOPICS
} from '../constants';

// ============ YARDIMCI FONKSÄ°YONLAR ============

/**
 * Tarih formatÄ±: YYYY-MM-DD
 */
export function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Tarihi parse et
 */
export function parseDate(dateStr: string): Date {
    return new Date(dateStr + 'T00:00:00');
}

/**
 * İki tarih arasındaki gün farkı
 */
function daysBetween(date1: Date, date2: Date): number {
    const time1 = date1.getTime();
    const time2 = date2.getTime();
    return Math.floor((time2 - time1) / (1000 * 60 * 60 * 24));
}

/**
 * Bugünün tarihini al
 */
export function getToday(): string {
    return formatDate(new Date());
}

/**
 * Gün kilitli mi kontrol et (saat 00:00'dan itibaren açılır - Proje isteği üzerine 01:00'dan 00:00'a çekildi)
 */
export function isDayUnlocked(date: string): boolean {
    const now = new Date();
    const targetDate = parseDate(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Geçmiş ise açık
    if (targetDate < today) {
        return true;
    }

    // Bugün ise
    if (formatDate(targetDate) === formatDate(today)) {
        const unlockTime = new Date(today);
        unlockTime.setHours(PLANNER_SETTINGS.unlockHour, PLANNER_SETTINGS.unlockMinute, 0, 0);
        return now >= unlockTime;
    }

    return false; // Gelecek gün - kilitli
}

/**
 * Haftanın başlangıcını (Pazartesi) al
 */
function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Pazartesi'ye ayarla
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

/**
 * Ayın başlangıcını al
 */
function getStartOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Günün adını al
 */
export function getDayName(date: string): DayOfWeek {
    const d = parseDate(date);
    return DAY_NAMES[d.getDay()] as DayOfWeek;
}

// ============ KONU DAĞITIMI ============

/**
 * Alana göre dersleri getir
 */
function getSubjectsForField(
    department: Department | undefined,
    phase: 'tyt' | 'ayt' | 'review',
    mode: 'school' | 'yks' | 'lgs'
): string[] {
    if (mode === 'lgs') {
        return SUBJECTS_BY_FIELD.LGS;
    }

    if (mode === 'school' || !department) {
        return SUBJECTS_BY_FIELD.general;
    }

    // Alan bazlı ders listesi
    const fieldKey = department === 'Sayısal' ? 'SAY' :
        department === 'Eşit Ağırlık' ? 'EA' :
            department === 'Sözel' ? 'SOZ' : 'DIL';

    const fieldSubjects = SUBJECTS_BY_FIELD[fieldKey];

    if (phase === 'tyt' || phase === 'review') {
        return fieldSubjects.tyt || SUBJECTS_BY_FIELD.general;
    }

    if (phase === 'ayt') {
        // AYT'de hem TYT hem AYT derslerini karıştır
        const fieldsWithYdt = fieldSubjects as { tyt: string[]; ydt?: string[]; ayt?: string[] };
        const aytSubjects = fieldKey === 'DIL' ? (fieldsWithYdt.ydt || []) : (fieldsWithYdt.ayt || []);
        return [...new Set([...aytSubjects, ...fieldSubjects.tyt.slice(0, 3)])];
    }

    return SUBJECTS_BY_FIELD.general;
}

/**
 * Dersin konularını getir
 */
function getTopicsForSubject(
    subject: string,
    examType: 'TYT' | 'AYT' | 'YDT' | 'LGS'
): string[] {
    const topics = EXAM_TOPICS[examType];
    if (!topics) return [];

    // Ders adını normalize et
    const normalizedSubject = subject.replace('-1', '').replace('-2', '').trim();

    return topics[normalizedSubject] || topics[subject] || [];
}

/**
 * Tamamlanmamış konuları filtrele
 */
function getIncompleteTopics(
    allTopics: string[],
    completedTopics: string[]
): string[] {
    return allTopics.filter(topic => !completedTopics.includes(topic));
}

// ============ GÜNLÜK PLAN OLUŞTURMA ============

/**
 * Tempo'ya göre günlük ders sayısını hesapla
 */
function getDailySubjectCount(tempo: StudyTempo, customCount?: number): number {
    if (customCount !== undefined) {
        const config = TEMPO_CONFIG[tempo];
        return Math.max(config.min, Math.min(config.max, customCount));
    }
    return TEMPO_CONFIG[tempo].default;
}

/**
 * Benzersiz görev ID'si oluştur
 */
function generateTaskId(): string {
    return `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Tek bir günün görevlerini oluştur
 * Only uses admin-defined curriculum tasks, no AI fallback
 */
function generateDailyTasks(
    date: string,
    config: SmartPlannerConfig,
    completedTopics: string[],
    order: number = 0,
    templateTasks?: any[] // Tasks from Admin curriculum
): DailyTask[] {
    // Only use admin-defined tasks - no AI fallback
    if (!templateTasks || templateTasks.length === 0) {
        return [];
    }

    return templateTasks.map((t, index) => ({
        id: generateTaskId(),
        date,
        subject: t.subject,
        topic: t.topic,
        examType: t.examType,
        taskType: t.taskType || 'both',
        videoDuration: t.videoDuration || 30,
        questionCount: t.questionCount || 40,
        status: isDayUnlocked(date) ? 'available' : 'locked',
        points: (t.videoDuration ? PLANNER_SETTINGS.pointsPerVideo : 0) + (t.questionCount ? PLANNER_SETTINGS.pointsPerQuestion : 0),
        order: order + index,
        videoCompleted: false,
        questionsCompleted: false
    }));
}

/**
 * Birden fazla gÃ¼nÃ¼n planÄ±nÄ± oluÅŸtur
 */
export function generateDailyPlans(
    config: SmartPlannerConfig,
    completedTopics: string[],
    daysToGenerate: number = 14, // VarsayÄ±lan 2 hafta
    curriculums: StudyPlanCurriculum[] = [] // Admin-managed master plans
): DailyPlan[] {
    const plans: DailyPlan[] = [];
    const startDate = parseDate(config.startDate);

    for (let i = 0; i < daysToGenerate; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        const dateStr = formatDate(currentDate);
        const dayName = getDayName(dateStr);

        // Calculate ISO week number of the year to match admin-defined curriculum weeks
        const getISOWeekNumber = (date: Date): number => {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const dayNum = d.getUTCDay() || 7;
            d.setUTCDate(d.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
        };

        const weekNumber = getISOWeekNumber(currentDate);
        const curriculum = curriculums.find(c => c.weekNumber === weekNumber);
        const templateTasks = curriculum?.days?.[dayName]?.tasks;

        const tasks = generateDailyTasks(dateStr, config, completedTopics, i * 10, templateTasks);
        const totalPoints = tasks.reduce((sum, t) => sum + t.points, 0);

        plans.push({
            date: dateStr,
            dayOfWeek: getDayName(dateStr),
            tasks,
            totalPoints,
            completedPoints: 0,
            isUnlocked: isDayUnlocked(dateStr),
            unlockTime: `${String(PLANNER_SETTINGS.unlockHour).padStart(2, '0')}:${String(PLANNER_SETTINGS.unlockMinute).padStart(2, '0')}`
        });
    }

    return plans;
}

// ============ GÃ–REV YÃ–NETÄ°MÄ° ============

/**
 * GÃ¶revi tamamla
 */
export function completeTask(
    plan: DailyPlan,
    taskId: string,
    completionType: 'video' | 'question' | 'both'
): { updatedPlan: DailyPlan; pointsEarned: number } {
    const taskIndex = plan.tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) {
        return { updatedPlan: plan, pointsEarned: 0 };
    }

    const task = { ...plan.tasks[taskIndex] };
    let pointsEarned = 0;

    switch (completionType) {
        case 'video':
            if (!task.videoCompleted) {
                task.videoCompleted = true;
                pointsEarned += PLANNER_SETTINGS.pointsPerVideo;
            }
            break;
        case 'question':
            if (!task.questionsCompleted) {
                task.questionsCompleted = true;
                pointsEarned += PLANNER_SETTINGS.pointsPerQuestion;
            }
            break;
        case 'both':
            if (!task.videoCompleted) {
                task.videoCompleted = true;
                pointsEarned += PLANNER_SETTINGS.pointsPerVideo;
            }
            if (!task.questionsCompleted) {
                task.questionsCompleted = true;
                pointsEarned += PLANNER_SETTINGS.pointsPerQuestion;
            }
            break;
    }

    // Tam tamamlandÄ±ÄŸÄ±nda bonus
    if (task.videoCompleted && task.questionsCompleted) {
        task.status = 'completed';
        task.completedAt = new Date().toISOString();
        pointsEarned += PLANNER_SETTINGS.pointsPerComplete;
    } else {
        task.status = 'in_progress';
    }

    const updatedTasks = [...plan.tasks];
    updatedTasks[taskIndex] = task;

    return {
        updatedPlan: {
            ...plan,
            tasks: updatedTasks,
            completedPoints: plan.completedPoints + pointsEarned
        },
        pointsEarned
    };
}

/**
 * Eksik gÃ¶revleri getir (telafi iÃ§in)
 */
export function getIncompleteTasks(plans: DailyPlan[]): DailyTask[] {
    const today = getToday();
    const incompleteTasks: DailyTask[] = [];

    plans.forEach(plan => {
        if (plan.date < today && plan.isUnlocked) {
            plan.tasks.forEach(task => {
                if (task.status !== 'completed') {
                    incompleteTasks.push({
                        ...task,
                        status: 'incomplete',
                        originalDate: task.originalDate || plan.date
                    });
                }
            });
        }
    });

    return incompleteTasks;
}

/**
 * Telafi gÃ¶revi ekle
 */
export function addMakeupTask(
    plans: DailyPlan[],
    task: DailyTask,
    targetDate: string,
    targetTime?: string
): DailyPlan[] {
    const planIndex = plans.findIndex(p => p.date === targetDate);
    if (planIndex === -1) return plans;

    const makeupTask: DailyTask = {
        ...task,
        id: generateTaskId(),
        date: targetDate,
        status: isDayUnlocked(targetDate) ? 'available' : 'locked',
        isMakeup: true,
        originalDate: task.date,
        order: plans[planIndex].tasks.length
    };

    const updatedPlans = [...plans];
    updatedPlans[planIndex] = {
        ...updatedPlans[planIndex],
        tasks: [...updatedPlans[planIndex].tasks, makeupTask],
        totalPoints: updatedPlans[planIndex].totalPoints + makeupTask.points
    };

    return updatedPlans;
}

// ============ KONFÄ°GÃœRASYON ============

/**
 * VarsayÄ±lan planlayÄ±cÄ± konfigÃ¼rasyonu oluÅŸtur
 */
export function createDefaultConfig(
    gradeLevel: string,
    department?: Department
): SmartPlannerConfig {
    const gradeConfig = GRADE_CONFIG[gradeLevel] || GRADE_CONFIG['12. SÄ±nÄ±f'];

    return {
        mode: gradeConfig.mode,
        tempo: gradeConfig.defaultTempo,
        dailySubjectCount: TEMPO_CONFIG[gradeConfig.defaultTempo].default,
        gradeLevel,
        department,
        startDate: getToday(),
        tytCompletionDays: YKS_TIMELINE.tytDays,
        aytCompletionDays: YKS_TIMELINE.aytDays,
        currentPhase: 'tyt',
        isActive: true
    };
}

/**
 * Ders iÃ§in renk al
 */
export function getSubjectColor(subject: string): string {
    return SUBJECT_COLORS[subject] || 'from-gray-500 to-slate-600';
}

/**
 * Dinamik kamp planı oluşturucu
 * Her ders için haftanın günlerini dikkate alarak plan oluşturur
 * selectedTopicIndices: Seçili konu indekslerini filtreler (kısmen devam eden öğrenciler için)
 * targetWeeks: Hedef hafta sayısına göre hız otomatik hesaplanır
 */
export function generateDynamicCampSchedule(campConfig: any, channels: any[], booksQuestionCounts: any[]) {
    if (!campConfig.startDate) return [];

    const subjects = ['math', 'physics', 'chemistry', 'biology', 'turkish', 'history', 'geography'];
    
    const getRawChannelData = (channelName: string) => {
        const chan = channels.find(c => c.name === channelName);
        if (chan) return chan.data;
        return campConfig.customChannels?.[channelName]?.data || [];
    };

    const startObj = new Date(campConfig.startDate);
    const startWeekdayIndex = startObj.getDay(); 

    const subjectState: any = {};
    subjects.forEach(sub => {
        if (campConfig.enabledSubjects && campConfig.enabledSubjects[sub] && campConfig[`selected${sub.charAt(0).toUpperCase() + sub.slice(1)}`]) {
            const channelSelection = campConfig[`selected${sub.charAt(0).toUpperCase() + sub.slice(1)}`];
            const channelSelection2 = campConfig[`selected${sub.charAt(0).toUpperCase() + sub.slice(1)}2`];
            const channelData = getRawChannelData(channelSelection);
            const channelData2 = channelSelection2 ? getRawChannelData(channelSelection2) : [];
            
            const merged: any[] = [];
            
            const processData = (data: any[]) => {
                data.forEach((item: any) => {
                    if (!item.day) return;
                    
                    let duration = item.total_duration || 0;
                    if (!duration && item.topic) {
                        const match = item.topic.match(/\((\d+)\s*dk\)/);
                        if (match) duration = parseInt(match[1]) || 0;
                    }

                    const existing = merged.find(x => x.day === item.day);
                    if (existing) {
                        existing.topic += ' | ' + (item.topic || '');
                        if (item.videos) existing.videos.push(...item.videos);
                        else existing.videos.push(item.topic);
                        existing.total_duration += duration;
                    } else {
                        merged.push({
                            day: item.day,
                            topic: item.topic || '',
                            videos: item.videos ? [...item.videos] : [item.topic],
                            total_duration: duration
                        });
                    }
                });
            };

            processData(channelData);
            if (channelData2.length > 0) {
                processData(channelData2);
            }
            
            merged.sort((a,b) => a.day - b.day);

            // ── Konu Filtreleme: selectedTopicIndices ile sadece seçili konuları dahil et ──
            const selectedIndices = campConfig.selectedTopicIndices?.[sub];
            let filteredData = merged;
            if (selectedIndices && Array.isArray(selectedIndices) && selectedIndices.length > 0) {
                // selectedIndices, merged dizisindeki indeksleri temsil eder
                filteredData = merged.filter((_: any, idx: number) => selectedIndices.includes(idx));
            }
            
            const combinedChannelSelection = channelSelection2 ? `${channelSelection} & ${channelSelection2}` : channelSelection;

            const schedule = campConfig.subjectSchedules?.[sub] || [0,1,2,3,4,5,6];

            subjectState[sub] = {
                data: filteredData,
                currentIndex: 0,
                schedule: schedule,
                channelSelection: combinedChannelSelection
            };
        }
    });

    // ── Hafta Bazlı Hız Hesaplama: targetWeeks > 0 ise otomatik speed hesapla ──
    const targetWeeks = campConfig.targetWeeks || 0;
    const calculatedSpeeds: { [key: string]: number } = {};
    
    if (targetWeeks > 0) {
        subjects.forEach(sub => {
            const state = subjectState[sub];
            if (!state) return;
            
            const schedule = state.schedule;
            // Haftada bu ders kaç gün çalışılacak
            const uniqueDaysPerWeek = new Set(schedule).size;
            // Hedef sürede toplam çalışma günü
            const totalAvailableDays = targetWeeks * uniqueDaysPerWeek;
            // Toplam konu sayısı
            const totalTopics = state.data.length;
            
            if (totalAvailableDays > 0 && totalTopics > 0) {
                // Günlük kaç konu işlenmeli
                calculatedSpeeds[sub] = Math.max(1, Math.ceil(totalTopics / totalAvailableDays));
            }
        });
    }

    const generatedDays: any[] = [];
    let currentDayOffset = 0;
    
    // targetWeeks varsa maksimum gün sınırı
    const maxDays = targetWeeks > 0 ? targetWeeks * 7 : 700;
    
    while (true) {
        let anyActive = false;
        const currentWeekday = (startWeekdayIndex + currentDayOffset) % 7;
        
        const daySchedule: any = {
            math: null,
            physics: null,
            chemistry: null,
            biology: null,
            turkish: null,
            history: null,
            geography: null,
            totalVideoMinutes: 0,
            totalQuestions: 0,
            activeSubjects: 0
        };

        subjects.forEach(sub => {
            const state = subjectState[sub];
            if (state) {
                if (state.currentIndex < state.data.length) {
                    anyActive = true;
                    // Check if subject is scheduled for today
                    if (state.schedule.includes(currentWeekday)) {
                        // targetWeeks varsa hesaplanan hızı kullan, yoksa manuel hızı kullan
                        const speed = targetWeeks > 0 && calculatedSpeeds[sub] 
                            ? calculatedSpeeds[sub] 
                            : (campConfig.subjectSpeeds?.[sub] || 1);
                        let combinedDuration = 0;
                        let combinedQuestions = 0;
                        let combinedVideos: string[] = [];
                        let combinedTopics: string[] = [];
                        let bkId = campConfig.sourceBooks?.[sub] || '';

                        for (let s = 0; s < speed; s++) {
                            if (state.currentIndex >= state.data.length) break;
                            const item = state.data[state.currentIndex];

                            let qTarget = campConfig.targetQuestions?.[sub] || 0;
                            if (bkId) {
                                const bookCount = booksQuestionCounts.find(b => b.bookId === bkId);
                                if (bookCount && bookCount.topics && bookCount.topics[item.topic] !== undefined) {
                                    qTarget = bookCount.topics[item.topic];
                                }
                            }

                            combinedDuration += item.total_duration || 0;
                            combinedQuestions += qTarget;
                            if (item.videos) combinedVideos.push(...item.videos);
                            if (item.topic) combinedTopics.push(item.topic);
                            
                            state.currentIndex++;
                        }

                        if (combinedTopics.length > 0) {
                            daySchedule[sub] = {
                                topic: combinedTopics.join(' | '),
                                duration: combinedDuration,
                                videos: combinedVideos,
                                questions: combinedQuestions,
                                bookId: bkId,
                                channelName: state.channelSelection
                            };
                            daySchedule.totalVideoMinutes += combinedDuration;
                            daySchedule.totalQuestions += combinedQuestions;
                            daySchedule.activeSubjects++;
                        } else {
                            daySchedule[sub] = { isFinished: true, topic: `${sub.toUpperCase()} Bitti! (Tekrar & Deneme)`, duration: 0, videos: [] };
                        }
                    } else {
                        daySchedule[sub] = { isExcluded: true };
                    }
                } else {
                    daySchedule[sub] = { isFinished: true, topic: `${sub.toUpperCase()} Bitti! (Tekrar & Deneme)`, duration: 0, videos: [] };
                }
            } else {
                daySchedule[sub] = { isExcluded: true };
            }
        });

        if (!anyActive) break; 

        generatedDays.push(daySchedule);
        currentDayOffset++;
        
        // Safety break
        if (currentDayOffset > maxDays) {
            if (targetWeeks > 0) {
                // Hedef hafta sınırına ulaşıldı - normal durum
            } else {
                console.warn("Dinamik kamp plani cok uzadi, guvenlik icin durduruldu (700 gun max).");
            }
            break;
        }
    }
    
    return generatedDays;
}

